// src/solo/BookingSettings.jsx
//
// Settings page for the public booking feature — lives inside Solo dashboard.
// Uses MUI to match the rest of the Solo UI.
//
// Add the route in App.js (inside the SoloLayout wrapper):
//   <Route path="/solo/booking-settings" element={<BookingSettings />} />
//
// Add a sidebar link in SoloLayout.jsx under the Tools section, e.g.:
//   { label: 'Booking Settings', to: '/solo/booking-settings', icon: <EventIcon /> }
//
// Day-location V1 (Aug 2026):
//   - Adds a freeform "Location" text field per open working day, stored
//     as day_locations alongside working_hours on the same settings row.
//   - Deliberately freeform text, not a dropdown/select — V1 scope is a
//     simple label ('Salon A' / 'Salon B') for a solo practitioner
//     splitting their week across two known locations. No separate
//     locations table, no picker.
//   - This value feeds into the Bookings.jsx diary's day-header label.

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, Switch, FormControlLabel,
  MenuItem, Button, Alert, Chip, Grid, Divider, CircularProgress,
  InputAdornment,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from '../axiosConfig';

const DAYS = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday'],
];

const PLACEHOLDERS = ['{client_name}', '{service}', '{date}', '{time}', '{stylist}'];

export default function BookingSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Public URL base — adjust if you want to read from an env var
  const publicBase =
    (typeof window !== 'undefined' ? window.location.origin : '') + '/book/';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/solo/booking-settings');
      setSettings(res.data);
    } catch (e) {
      setError('Could not load booking settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();

    // If returning from Stripe onboarding, re-check status
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'return') {
      axios.get('/solo/booking-settings/stripe-status').finally(load);
      // Clean up the URL
      window.history.replaceState({}, '', '/solo/booking-settings');
    }
  }, [load]);

  const update = (patch) => {
    setSettings((s) => ({ ...s, ...patch }));
  };

  const updateHours = (day, idx, value) => {
    setSettings((s) => {
      const wh = { ...(s.working_hours || {}) };
      const arr = [...(wh[day] || ['09:00', '17:00'])];
      if (arr.length < 2) arr.push('', '');
      arr[idx] = value;
      wh[day] = arr.slice(0, 2);
      return { ...s, working_hours: wh };
    });
  };

  const toggleDay = (day, closed) => {
    setSettings((s) => {
      const wh = { ...(s.working_hours || {}) };
      wh[day] = closed ? [] : ['09:00', '17:00'];
      return { ...s, working_hours: wh };
    });
  };

  // Day-location V1 — updates the freeform location label for one day.
  const updateLocation = (day, value) => {
    setSettings((s) => ({
      ...s,
      day_locations: { ...(s.day_locations || {}), [day]: value },
    }));
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        is_bookings_enabled: settings.is_bookings_enabled,
        booking_url_slug: settings.booking_url_slug,
        business_name: settings.business_name,
        bio: settings.bio,
        deposit_type: settings.deposit_type,
        deposit_value: Number(settings.deposit_value || 0),
        cancellation_hours: Number(settings.cancellation_hours || 24),
        buffer_minutes: Number(settings.buffer_minutes || 0),
        advance_booking_days: Number(settings.advance_booking_days || 60),
        sms_confirmation_template: settings.sms_confirmation_template,
        sms_reminder_template: settings.sms_reminder_template,
        working_hours: settings.working_hours,
        day_locations: settings.day_locations,
      };
      const res = await axios.put('/solo/booking-settings', payload);
      setSettings(res.data);
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save settings');
    } finally {
      setSaving(false);
    }
  };

  const connectStripe = async () => {
    try {
      const res = await axios.post('/solo/booking-settings/stripe-connect');
      window.location.href = res.data.onboarding_url;
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start Stripe onboarding');
    }
  };

  const openStripeDashboard = async () => {
    try {
      const res = await axios.get('/solo/booking-settings/stripe-login');
      window.open(res.data.url, '_blank');
    } catch (e) {
      setError('Could not open Stripe dashboard');
    }
  };

  if (loading || !settings) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const canEnable = settings.stripe_onboarded || settings.deposit_type === 'none';

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>Online Booking</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Let clients book online and pay a deposit in advance.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* --- Master toggle --- */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={!!settings.is_bookings_enabled}
              disabled={!canEnable}
              onChange={(e) =>
                update({ is_bookings_enabled: e.target.checked })
              }
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Booking page live
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {settings.is_bookings_enabled
                  ? `Live at ${publicBase}${settings.booking_url_slug}`
                  : 'Your booking page is currently offline'}
              </Typography>
            </Box>
          }
        />
        {!canEnable && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Connect Stripe below before enabling bookings with a deposit.
          </Alert>
        )}
        {settings.is_bookings_enabled && (
          <Button
            sx={{ mt: 2 }}
            size="small"
            endIcon={<OpenInNewIcon fontSize="small" />}
            href={`${publicBase}${settings.booking_url_slug}`}
            target="_blank"
          >
            View my booking page
          </Button>
        )}
      </Paper>

      {/* --- Stripe Connect --- */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          Payments
        </Typography>
        {settings.stripe_onboarded ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography>Stripe account connected</Typography>
            </Box>
            <Button variant="outlined" onClick={openStripeDashboard}
              endIcon={<OpenInNewIcon fontSize="small" />}>
              Open Stripe dashboard
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Connect a Stripe account to accept deposits. Payments go directly
              to your bank account — SalonLogic Pro never holds the money.
            </Typography>
            <Button variant="contained" onClick={connectStripe}>
              {settings.stripe_account_id
                ? 'Continue Stripe setup'
                : 'Connect Stripe account'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* --- Page settings --- */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Page details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Business / display name"
              value={settings.business_name}
              onChange={(e) => update({ business_name: e.target.value })}
              helperText="Shown to clients on your booking page"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Booking page URL"
              value={settings.booking_url_slug}
              onChange={(e) =>
                update({
                  booking_url_slug: e.target.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]+/g, '-')
                    .replace(/^-+|-+$/g, ''),
                })
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">{publicBase}</InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Short bio (optional)"
              value={settings.bio}
              onChange={(e) => update({ bio: e.target.value })}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* --- Deposit & policies --- */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Deposit & policies
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select fullWidth
              label="Deposit type"
              value={settings.deposit_type}
              onChange={(e) => update({ deposit_type: e.target.value })}
            >
              <MenuItem value="none">No deposit</MenuItem>
              <MenuItem value="fixed">Fixed amount (£)</MenuItem>
              <MenuItem value="percent">Percentage of service</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="number"
              label={settings.deposit_type === 'percent' ? 'Percentage' : 'Amount'}
              value={settings.deposit_value}
              onChange={(e) => update({ deposit_value: e.target.value })}
              disabled={settings.deposit_type === 'none'}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {settings.deposit_type === 'percent' ? '%' : '£'}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth type="number" label="Cancellation notice"
              value={settings.cancellation_hours}
              onChange={(e) => update({ cancellation_hours: e.target.value })}
              InputProps={{ endAdornment:
                <InputAdornment position="end">hours</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="number" label="Buffer between appointments"
              value={settings.buffer_minutes}
              onChange={(e) => update({ buffer_minutes: e.target.value })}
              InputProps={{ endAdornment:
                <InputAdornment position="end">minutes</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth type="number" label="Advance booking window"
              value={settings.advance_booking_days}
              onChange={(e) => update({ advance_booking_days: e.target.value })}
              InputProps={{ endAdornment:
                <InputAdornment position="end">days ahead</InputAdornment> }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* --- Working hours --- */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Working hours
        </Typography>
        {DAYS.map(([key, label]) => {
          const hours = settings.working_hours?.[key] || [];
          const closed = hours.length !== 2;
          const location = settings.day_locations?.[key] || '';
          return (
            <Box
              key={key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ width: 100 }}>{label}</Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={!closed}
                    onChange={(e) => toggleDay(key, !e.target.checked)}
                    size="small"
                  />
                }
                label={closed ? 'Closed' : 'Open'}
                sx={{ minWidth: 120 }}
              />
              {!closed && (
                <>
                  <TextField
                    type="time" size="small"
                    value={hours[0] || ''}
                    onChange={(e) => updateHours(key, 0, e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <Typography variant="body2">to</Typography>
                  <TextField
                    type="time" size="small"
                    value={hours[1] || ''}
                    onChange={(e) => updateHours(key, 1, e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    size="small"
                    label="Location"
                    placeholder="e.g. Salon A"
                    value={location}
                    onChange={(e) => updateLocation(key, e.target.value)}
                    sx={{ width: 160 }}
                  />
                </>
              )}
            </Box>
          );
        })}
      </Paper>

      {/* --- SMS templates --- */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          SMS templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use these placeholders — they'll be replaced automatically:
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
          {PLACEHOLDERS.map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" />
          ))}
        </Box>
        <TextField
          fullWidth multiline minRows={3}
          label="Confirmation SMS (sent when booking is confirmed)"
          value={settings.sms_confirmation_template}
          onChange={(e) =>
            update({ sms_confirmation_template: e.target.value })
          }
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth multiline minRows={3}
          label="Reminder SMS (sent 48 hours before appointment)"
          value={settings.sms_reminder_template}
          onChange={(e) =>
            update({ sms_reminder_template: e.target.value })
          }
        />
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, position: 'sticky', bottom: 16 }}>
        <Button
          variant="contained" size="large" onClick={save}
          disabled={saving}
          sx={{ boxShadow: 3 }}
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </Box>
    </Box>
  );
}